import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useGlobalLoader } from "@/components/GlobalLoader";
import { useAuth } from "@/hooks/useAuth";
import { alert } from "@/contexts/AlertModalContext";
import inovaone_api from "@/services/inovaone_api";
import type { SerialResponseItem } from "@/types/serial-response-item";
import type { NewSerialItem } from "@/types/new-serial-item";
import { retry } from "@/utils/retry";

export type SeriaisContextType = {
  seriais: SerialResponseItem[];
  apagarSerial: (serial: SerialResponseItem) => void;
  coletaSeriais: () => Promise<void>;
  editarSeriais: (
    seriais: SerialResponseItem[],
  ) => Promise<SerialResponseItem[]>;
  criarSeriais: (seriais: NewSerialItem[]) => Promise<SerialResponseItem[]>;
  adicionarMaisUmNosSeriais: (seriais_ean: string[]) => Promise<void>;
  obterSerialAtual: (gtin: string) => SerialResponseItem | undefined;
};

export const SeriaisContext = createContext<SeriaisContextType | undefined>(
  undefined,
);

export function SeriaisProvider({ children }: { children: ReactNode }) {
  const { setIsLoading } = useGlobalLoader();
  const { usuario } = useAuth();
  const [seriais, setSeriais] = useState<SerialResponseItem[]>([]);
  const seriaisRef = useRef<SerialResponseItem[]>([]);

  useEffect(() => {
    seriaisRef.current = seriais;
  }, [seriais]);

  // Lê do ref para garantir consistência mesmo entre chamadas síncronas
  // antes do estado React propagar.
  const obterSerialAtual = (gtin: string) =>
    seriaisRef.current.find((s) => s.gtin === gtin);

  const aplicarSeriaisLocal = (atualizados: SerialResponseItem[]) => {
    const map = new Map(seriaisRef.current.map((s) => [s.gtin, s]));
    for (const novo of atualizados) {
      const existente = map.get(novo.gtin);
      if (!existente || (novo.serial ?? 0) >= (existente.serial ?? 0)) {
        map.set(novo.gtin, novo);
      }
    }
    const arr = Array.from(map.values());
    seriaisRef.current = arr;
    setSeriais(arr);
  };

  const criarSeriais = async (
    seriais_para_criar: NewSerialItem[],
  ): Promise<SerialResponseItem[]> => {
    if (seriais_para_criar.length === 0) return [];
    try {
      const res = await retry(
        () =>
          inovaone_api.post<SerialResponseItem[]>(
            `/serialGtin`,
            seriais_para_criar,
          ),
        { rotulo: "criarSeriais", tentativas: 3, delayMs: 400 },
      );
      aplicarSeriaisLocal(res.data);
      return res.data;
    } catch (error) {
      console.error("Erro ao criar serial:", error);
      alert.error("Erro ao criar serial. Tente novamente mais tarde.");
      throw error;
    }
  };

  const editarSeriais = async (
    seriais_para_atualizar: SerialResponseItem[],
  ): Promise<SerialResponseItem[]> => {
    if (seriais_para_atualizar.length === 0) return [];
    try {
      await retry(
        () =>
          inovaone_api.patch<SerialResponseItem>(
            `/serialGtin`,
            seriais_para_atualizar,
          ),
        { rotulo: "editarSeriais", tentativas: 3, delayMs: 400 },
      );
      aplicarSeriaisLocal(seriais_para_atualizar);
      return seriais_para_atualizar;
    } catch (error) {
      console.error("Erro ao editar serial:", error);
      alert.error("Erro ao editar serial. Tente novamente mais tarde.");
      throw error;
    }
  };

  const apagarMultiplosSeriais = async (
    seriais_para_apagar: SerialResponseItem[],
  ) => {
    setIsLoading(true);
    try {
      if (seriais_para_apagar.length === 0) return;
      await inovaone_api.post(`/serialGtin/delete`, {
        ids: seriais_para_apagar.map((s) => s._id),
      });
      alert.success("Seriais apagados com sucesso!");
    } catch (error) {
      console.error("Erro ao apagar seriais:", error);
      alert.error("Erro ao apagar seriais. Tente novamente mais tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  const apagarSerial = async (serial: SerialResponseItem) => {
    setIsLoading(true);
    try {
      await inovaone_api.delete(`/serialGtin/${serial._id}`);
      setSeriais((prevSeriais) =>
        prevSeriais.filter((s) => s._id !== serial._id),
      );
      alert.success("Serial apagado com sucesso!");
    } catch (error) {
      console.error("Erro ao apagar serial:", error);
      alert.error("Erro ao apagar serial. Tente novamente mais tarde.");
    } finally {
      setIsLoading(false);
    }
  };

  const adicionarMaisUmNosSeriais = async (seriais_ean: string[]) => {
    for (const serial_ean of seriais_ean) {
      const serial_para_atualizar = obterSerialAtual(serial_ean);
      if (serial_para_atualizar) {
        await editarSeriais([
          {
            ...serial_para_atualizar,
            serial: serial_para_atualizar.serial + 1,
          },
        ]);
      }
    }
  };

  const coletaSeriais = async () => {
    try {
      const response_seriais = await retry(
        () =>
          inovaone_api.get<SerialResponseItem[]>(
            `/serialGtin/*/${usuario?.id_conta}`,
          ),
        { rotulo: "coletaSeriais", tentativas: 3, delayMs: 400 },
      );

      // Agrupar por GTIN e encontrar duplicados, mantendo o maior serial.
      const gtinsProcessados = new Set<string>();
      const seriaisADeletar: SerialResponseItem[] = [];

      for (const serial of response_seriais.data) {
        if (gtinsProcessados.has(serial.gtin)) continue;
        gtinsProcessados.add(serial.gtin);

        const seriais_iguais = response_seriais.data.filter(
          (s) => s.gtin === serial.gtin,
        );

        if (seriais_iguais.length > 1) {
          const ordenados = seriais_iguais.sort((a, b) => a.serial - b.serial);
          const menores = ordenados.slice(0, ordenados.length - 1);
          seriaisADeletar.push(...menores);
        }
      }

      if (seriaisADeletar.length) {
        await apagarMultiplosSeriais(seriaisADeletar);
        const response_atualizado = await inovaone_api.get<
          SerialResponseItem[]
        >(`/serialGtin/*/${usuario?.id_conta}`);
        seriaisRef.current = response_atualizado.data;
        setSeriais(response_atualizado.data);
      } else {
        seriaisRef.current = response_seriais.data;
        setSeriais(response_seriais.data);
      }
    } catch (error) {
      console.error("Erro ao coletar seriais:", error);
      alert.error("Erro ao coletar seriais. Tente novamente mais tarde.");
    }
  };

  return (
    <SeriaisContext.Provider
      value={{
        seriais,
        apagarSerial,
        coletaSeriais,
        editarSeriais,
        criarSeriais,
        adicionarMaisUmNosSeriais,
        obterSerialAtual,
      }}
    >
      {children}
    </SeriaisContext.Provider>
  );
}

export function useSeriais() {
  const context = useContext(SeriaisContext);
  if (!context) {
    throw new Error("useSeriais deve ser usado dentro de um SeriaisProvider");
  }
  return context;
}
